import React from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  styled,
  CircularProgress,
  Chip,
  IconButton,
  Button,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Search as SearchIcon,
  LocalOffer as PriceIcon,
  Category as CategoryIcon,
  History as HistoryIcon,
  Clear as ClearIcon,
  DeleteOutline as DeleteIcon,
  TrendingUp as TrendingIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { keyframes } from "@mui/system";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SuggestionsContainer = styled(Box)(({ theme, isMobile }) => ({
  position: "absolute",
  top: isMobile ? "100%" : "calc(100% + 8px)",
  left: 0,
  right: 0,
  backgroundColor: theme.palette.background.paper,
  borderRadius: isMobile ? 0 : theme.shape.borderRadius * 2,
  boxShadow: isMobile ? "none" : theme.shadows[4],
  zIndex: theme.zIndex.modal,
  maxHeight: isMobile ? "calc(100vh - 120px)" : "400px",
  overflowY: "auto",
  border: isMobile ? "none" : `1px solid ${alpha(theme.palette.divider, 0.8)}`,
  animation: `${fadeIn} 0.2s ease-out`,
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: alpha(theme.palette.background.default, 0.5),
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.text.disabled, 0.5),
    borderRadius: "6px",
    "&:hover": {
      backgroundColor: alpha(theme.palette.text.secondary, 0.5),
    },
  },
  
  ...(isMobile
    ? {
      borderTop: `1px solid ${theme.palette.divider}`,
    }
    : {
      marginTop: theme.spacing(1),
    }),
}));

const StyledListItem = styled(ListItem)(({ theme, isMobile }) => ({
  padding: theme.spacing(1.5, 2),
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: alpha(theme.palette.action.hover, 0.7),
    transform: "translateY(-1px)",
    boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.05)}`,
  },
  "&:active": {
    transform: "translateY(0)",
    boxShadow: "none",
  },
  display: "flex",
  alignItems: "flex-start",
  width: "100%",
  ...(isMobile && {
    padding: theme.spacing(1, 1.5),
  }),
}));

const BookCover = styled(Avatar)(({ theme }) => ({
  width: 60,
  height: 80,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: theme.shadows[4],
  },
  
  [theme.breakpoints.down("sm")]: {
    width: 48,
    height: 64,
  },
  
  [theme.breakpoints.down(400)]: {
    width: 40,
    height: 56,
  },
}));

const BookInfo = styled(Box)(({ theme, isMobile }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  flex: 1,
  minWidth: 0,
  ...(isMobile && {
    width: "calc(100% - 80px)",
    maxWidth: "100%",
    overflow: "hidden"
  }),
  
  [theme.breakpoints.down(400)]: {
    gap: theme.spacing(0.25),
  }
}));

const MetaInfo = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginTop: theme.spacing(0.5),
  flexWrap: "wrap",
  
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.25),
  },
  
  [theme.breakpoints.down(400)]: {
    gap: theme.spacing(0.5),
  },
}));

const PriceChip = styled(Chip)(({ theme, discounted, isMobile }) => ({
  backgroundColor: discounted
    ? alpha(theme.palette.success.light, 0.9)
    : alpha(theme.palette.grey[100], 0.9),
  color: discounted ? theme.palette.success.dark : theme.palette.text.primary,
  fontWeight: 500,
  height: 24,
  transition: "transform 0.2s ease",
  
  "&:hover": {
    transform: "translateY(-1px)",
  },
  
  ...(isMobile && {
    height: 20,
    '& .MuiChip-label': {
      fontSize: '0.7rem',
      padding: '0 6px',
    },
    '& .MuiChip-icon': {
      fontSize: 14,
    }
  }),
  
  [theme.breakpoints.down(400)]: {
    height: 18,
    '& .MuiChip-label': {
      fontSize: '0.65rem',
      padding: '0 4px',
    },
    '& .MuiChip-icon': {
      fontSize: 12,
    }
  }
}));

const CategoryChip = styled(Chip)(({ theme, isMobile }) => ({
  backgroundColor: alpha(theme.palette.primary.light, 0.8),
  color: theme.palette.primary.dark,
  height: 24,
  transition: "transform 0.2s ease",
  
  "&:hover": {
    transform: "translateY(-1px)",
  },
  
  ...(isMobile && {
    height: 20,
    '& .MuiChip-label': {
      fontSize: '0.7rem',
      padding: '0 6px',
    },
    '& .MuiChip-icon': {
      fontSize: 14,
    }
  }),
  
  [theme.breakpoints.down(400)]: {
    height: 18,
    '& .MuiChip-label': {
      fontSize: '0.65rem',
      padding: '0 4px',
    },
    '& .MuiChip-icon': {
      fontSize: 12,
    }
  }
}));

const HistoryItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  cursor: "pointer",
  transition: "all 0.2s ease",
  
  "&:hover": {
    backgroundColor: alpha(theme.palette.action.hover, 0.7),
    "& .delete-button": {
      opacity: 1,
    },
  },
  
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.75, 1.5),
  },
  
  [theme.breakpoints.down(400)]: {
    padding: theme.spacing(0.5, 1),
  },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  opacity: 0,
  transition: "all 0.2s ease",
  padding: 4,
  marginLeft: theme.spacing(1),
  
  "&:hover": {
    backgroundColor: alpha(theme.palette.error.main, 0.1),
    color: theme.palette.error.main,
  },
  
  [theme.breakpoints.down("sm")]: {
    opacity: 0.6,
  },
}));

const TruncatedTypography = styled(Typography)(({ theme }) => ({
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  wordBreak: "break-word",
  lineHeight: "1.2em",
  maxHeight: "2.4em",
  width: "100%",
}));

const StyledAvatar = styled(Avatar)(({ theme, type }) => ({
  backgroundColor: type === 'search' 
    ? alpha(theme.palette.primary.light, 0.9) 
    : alpha(theme.palette.grey[300], 0.9),
  color: type === 'search' 
    ? theme.palette.primary.dark 
    : theme.palette.text.secondary,
  transition: "all 0.2s ease",
  
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.1)}`,
  },
  
  [theme.breakpoints.down("sm")]: {
    width: 32,
    height: 32,
  },
  
  [theme.breakpoints.down(400)]: {
    width: 28,
    height: 28,
  },
}));

const SpinnerContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(3),
  alignItems: "center",
  gap: theme.spacing(2),
  animation: `${fadeIn} 0.3s ease-out`,
}));

const HeaderContainer = styled(ListItem)(({ theme }) => ({
  justifyContent: "space-between",
  padding: theme.spacing(1.5, 2, 1, 2),
  position: "sticky",
  top: 0,
  backgroundColor: alpha(theme.palette.background.paper, 0.98),
  zIndex: 1,
  backdropFilter: "blur(8px)",
  borderBottom: `1px solid ${theme.palette.divider}`,
  
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1, 1.5, 0.75, 1.5),
  },
  
  [theme.breakpoints.down(400)]: {
    padding: theme.spacing(0.75, 1, 0.5, 1),
  },
}));

const ClearAllButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  padding: theme.spacing(0.5, 1),
  minWidth: "auto",
  fontSize: "0.75rem",
  transition: "all 0.2s ease",
  
  "&:hover": {
    backgroundColor: alpha(theme.palette.error.main, 0.1),
  },
  
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.25, 0.75),
    fontSize: "0.7rem",
  },
}));

const ViewAllItem = styled(StyledListItem)(({ theme }) => ({
  color: theme.palette.primary.main,
  backgroundColor: alpha(theme.palette.action.hover, 0.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  position: "sticky",
  bottom: 0,
  fontWeight: 500,
  
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
}));

const SearchSuggestions = ({
  results,
  loading,
  error,
  searchQuery,
  onSelect,
  onViewAll,
  isMobile = false,
  searchHistory = [],
  onHistoryItemClick,
  onHistoryItemDelete,
  onClearHistory,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down(400));
  
  if (!searchQuery) return null;

  const formatPrice = (price, originalPrice, isMobile) => {
    const hasDiscount = originalPrice > price;
    return (
      <MetaInfo>
        <PriceChip
          icon={<PriceIcon sx={{ fontSize: isMobile ? (isExtraSmall ? 12 : 14) : 16 }} />}
          label={`$${price}`}
          discounted={hasDiscount}
          size="small"
          isMobile={isMobile}
        />
        {hasDiscount && (
          <Typography
            variant="caption"
            sx={{
              textDecoration: "line-through",
              color: "text.secondary",
              fontSize: isMobile ? (isExtraSmall ? '0.6rem' : '0.65rem') : 'inherit',
            }}
          >
            ${originalPrice}
          </Typography>
        )}
      </MetaInfo>
    );
  };

  const renderHistory = () => (
    <List sx={{ p: 0 }}>
      <HeaderContainer>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HistoryIcon color="action" fontSize={isExtraSmall ? "small" : "medium"} />
          <Typography 
            variant={isExtraSmall ? "caption" : "subtitle2"}
            fontWeight={500}
          >
            {t('topBar.recentSearches')}
          </Typography>
        </Box>
        {searchHistory.length > 0 && (
          <ClearAllButton
            size="small"
            startIcon={<ClearIcon fontSize="small" />}
            onClick={onClearHistory}
            color="error"
            variant="text"
          >
            {t('topBar.clearAll')}
          </ClearAllButton>
        )}
      </HeaderContainer>
      {searchHistory.map((item) => (
        <HistoryItem
          key={item.timestamp}
          onClick={() => onHistoryItemClick(item.query)}
        >
          <ListItemAvatar sx={{ minWidth: isExtraSmall ? 36 : 40 }}>
            <StyledAvatar 
              sx={{ width: isExtraSmall ? 28 : 32, height: isExtraSmall ? 28 : 32 }}
              type="history"
            >
              <HistoryIcon fontSize={isExtraSmall ? "small" : "medium"} />
            </StyledAvatar>
          </ListItemAvatar>
          <ListItemText
            primary={item.query}
            primaryTypographyProps={{
              variant: "body2",
              fontSize: isExtraSmall ? "0.75rem" : "0.875rem",
              noWrap: true,
            }}
          />
          <DeleteButton
            className="delete-button"
            onClick={(e) => {
              e.stopPropagation();
              onHistoryItemDelete(item.query);
            }}
            size="small"
          >
            <DeleteIcon fontSize="small" />
          </DeleteButton>
        </HistoryItem>
      ))}
    </List>
  );

  return (
    <SuggestionsContainer isMobile={isMobile}>
      {loading ? (
        <SpinnerContainer>
          <CircularProgress 
            size={isExtraSmall ? 20 : 24} 
            thickness={isExtraSmall ? 4 : 3.6}
            color="primary"
          />
          <Typography 
            color="text.secondary" 
            variant={isExtraSmall ? "caption" : "body2"}
          >
            {t('topBar.search.searching')}
          </Typography>
        </SpinnerContainer>
      ) : error ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography color="error" variant={isExtraSmall ? "caption" : "body2"}>
            {t('topBar.search.error')}
          </Typography>
        </Box>
      ) : (
        <>
          {!searchQuery && searchHistory.length > 0 ? (
            renderHistory()
          ) : results?.length > 0 ? (
            <List sx={{ p: 0 }}>
              {results.slice(0, 5).map((book) => (
                <React.Fragment key={book.id}>
                  <StyledListItem 
                    onClick={() => onSelect(book)} 
                    isMobile={isMobile}
                  >
                    <ListItemAvatar
                      sx={{
                        minWidth: isMobile ? (isExtraSmall ? 50 : 58) : 76,
                        mr: isMobile ? 1 : 2,
                        flexShrink: 0,
                      }}
                    >
                      <BookCover
                        variant="rounded"
                        src={book.cover_image}
                        alt={book.title}
                        sx={isMobile ? { 
                          width: isExtraSmall ? 40 : 48, 
                          height: isExtraSmall ? 56 : 64 
                        } : undefined}
                      />
                    </ListItemAvatar>
                    <BookInfo isMobile={isMobile}>
                      <TruncatedTypography
                        variant={isMobile ? "body2" : "subtitle1"}
                        fontWeight={600}
                        sx={{
                          fontSize: isMobile ? (isExtraSmall ? "0.75rem" : "0.875rem") : "inherit",
                          mb: 0.5,
                          width: "100%",
                          color: "text.primary",
                          display: "block",
                        }}
                      >
                        {book.title}
                      </TruncatedTypography>
                      <TruncatedTypography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontSize: isMobile ? (isExtraSmall ? "0.675rem" : "0.75rem") : "inherit",
                          mb: 0.5,
                          width: "100%",
                        }}
                      >
                        {book.authors_display?.join(", ")}
                      </TruncatedTypography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: isMobile ? 0.5 : 1,
                          flexWrap: "wrap",
                          width: "100%",
                        }}
                      >
                        {formatPrice(book.price, book.original_price, isMobile)}
                        <CategoryChip
                          icon={<CategoryIcon sx={{ fontSize: isMobile ? (isExtraSmall ? 12 : 14) : 16 }} />}
                          label={book.category_display}
                          size="small"
                          isMobile={isMobile}
                        />
                      </Box>
                    </BookInfo>
                  </StyledListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))}
              <ViewAllItem
                onClick={onViewAll}
              >
                <ListItemAvatar sx={{ minWidth: isExtraSmall ? 36 : 46 }}>
                  <StyledAvatar
                    type="search"
                    sx={{ width: isExtraSmall ? 28 : 36, height: isExtraSmall ? 28 : 36 }}
                  >
                    <SearchIcon fontSize={isExtraSmall ? "small" : "medium"} />
                  </StyledAvatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${t('topBar.search.viewAll')} "${searchQuery}"`}
                  primaryTypographyProps={{
                    variant: "subtitle2",
                    fontWeight: 600,
                    sx: {
                      fontSize: isMobile ? (isExtraSmall ? "0.75rem" : "0.875rem") : "inherit",
                    },
                  }}
                />
                <TrendingIcon 
                  color="primary" 
                  fontSize={isExtraSmall ? "small" : "medium"}
                  sx={{ ml: 1, opacity: 0.8 }}
                />
              </ViewAllItem>
            </List>
          ) : (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography 
                color="text.secondary"
                variant={isExtraSmall ? "caption" : "body2"}
              >
                {t('topBar.search.noResults')} "{searchQuery}"
              </Typography>
            </Box>
          )}
        </>
      )}
    </SuggestionsContainer>
  );
};

export default SearchSuggestions;
